import { emailIsValid } from '@/components/signup/utils/email-is-valid';

/**
 * Common free / personal email providers. When a user signs up with an address
 * on one of these domains we treat it as a "personal" (non-work) email and can
 * nudge them toward using a work email instead.
 *
 * This list mirrors the intent of the data-side `signup_domain_type = 'PERSONAL'`
 * classification. It is intentionally conservative — only well-known consumer
 * providers are included so we don't mis-flag small-company domains.
 */
const PERSONAL_EMAIL_DOMAINS = new Set([
	// Google
	'gmail.com',
	'googlemail.com',
	// Microsoft
	'outlook.com',
	'hotmail.com',
	'live.com',
	'msn.com',
	// Yahoo
	'yahoo.com',
	'yahoo.co.uk',
	'yahoo.co.in',
	'ymail.com',
	'rocketmail.com',
	// Apple
	'icloud.com',
	'me.com',
	'mac.com',
	// Privacy-focused
	'proton.me',
	'protonmail.com',
	'pm.me',
	'tutanota.com',
	'tuta.io',
	// Other common consumer providers
	'aol.com',
	'gmx.com',
	'gmx.net',
	'mail.com',
	'zoho.com',
	'yandex.com',
	'yandex.ru',
	'fastmail.com',
	'hey.com',
]);

/**
 * Returns true when the supplied email address uses a known free / personal
 * email provider domain.
 *
 * Matching is case-insensitive and tolerant of surrounding whitespace. Any
 * input that is empty, malformed, or has no domain segment returns false (we do
 * not nudge when we can't confidently classify the domain).
 *
 * @param email - The full email address entered by the user.
 * @returns Whether the email domain is a known personal / free provider.
 */
export const isPersonalDomain = (email = ''): boolean => {
	const [localPart, domain, ...rest] = email.trim().toLowerCase().split('@');

	// Require a well-formed "local@domain" shape with exactly one "@" and a
	// non-empty local part before classifying the domain.
	if (!localPart || !domain || rest.length > 0) {
		return false;
	}

	return PERSONAL_EMAIL_DOMAINS.has(domain);
};

/**
 * Returns true when the email's domain, *as typed so far*, is confidently a
 * known personal / free provider — determined by matching the **complete**
 * domain (including its TLD) against the set of known provider domains.
 *
 * This powers a live, as-you-type nudge. We deliberately wait until the user has
 * typed the *full* provider domain before classifying, so we only nudge once
 * we're confident and the address is unambiguous. For example "ada@gmail.com"
 * qualifies, whereas earlier partials such as "ada@g", "ada@gmai", "ada@gmail"
 * and "ada@gmail." do NOT — the domain isn't complete yet (no ".com"). Lookalikes
 * such as "ada@gmailx.com" or "ada@my-company.com" never match.
 *
 * Matching is case-insensitive and tolerant of surrounding whitespace.
 *
 * @param email - The (possibly partial) email address entered by the user.
 * @returns Whether the typed domain is confidently a known personal / free
 *   provider domain.
 */
export const isLikelyPersonalDomain = (email = ''): boolean => {
	const [localPart, domain, ...rest] = email.trim().toLowerCase().split('@');

	// Need a non-empty local part, at least one domain character, and exactly
	// one "@" before we attempt to classify.
	if (!localPart || !domain || rest.length > 0) {
		return false;
	}

	// Require the *complete* domain (including the TLD, e.g. "gmail.com") to match
	// a known personal provider. We intentionally do NOT match on the leading
	// label alone, so the nudge only fires once the user has typed the full
	// domain — e.g. "ada@gmail" does not qualify, but "ada@gmail.com" does.
	return PERSONAL_EMAIL_DOMAINS.has(domain);
};

/**
 * Returns true when the supplied email is a *business* email — i.e. a
 * well-formed, complete email address whose domain is NOT a known personal /
 * free provider.
 *
 * This is a deliberately positive classification (rather than "not personal"):
 * it requires a fully valid email first, so partially-typed input and malformed
 * addresses are never treated as business. Mirrors the data-side
 * `signup_domain_type = 'BUSINESS'` intent, the counterpart to
 * {@link isPersonalDomain}.
 *
 * @param email - The full email address entered by the user.
 * @returns Whether the email is a valid address on a non-personal (business)
 *   domain.
 */
export const isBusinessDomain = (email = ''): boolean => {
	// Trim before validating so this helper is whitespace-tolerant like its
	// sibling `isPersonalDomain` (the underlying `emailIsValid` uses an anchored
	// regex and does not tolerate surrounding whitespace on its own).
	const trimmed = email.trim();
	return emailIsValid(trimmed) && !isPersonalDomain(trimmed);
};
